import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { IconAlertTriangle, IconDollar } from "../components/icons";
import { KpiTile, SparkLine, SplitMeter } from "../components/intelligence/KpiTile";
import { fmtCompactBRL } from "../components/intelligence/ExecutiveHero";
import type { TransportFinancialDto, CalculationMemoryDto } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

const AGING_COLORS = ["var(--brand)", "#F59E0B", "#F97316", "#DC2626", "#7C3AED"];

/** Largest-remainder rounding so pie percentages always sum to exactly 100. */
function roundToHundred(values: number[]): number[] {
  const total = values.reduce((s, v) => s + v, 0);
  if (total === 0) return values.map(() => 0);
  const floors = values.map((v) => Math.floor((v / total) * 100));
  const remainders = values.map((v, i) => (v / total) * 100 - floors[i]!);
  const deficit = 100 - floors.reduce((s, v) => s + v, 0);
  const order = remainders
    .map((r, i) => ({ r, i }))
    .sort((a, b) => b.r - a.r)
    .map((x) => x.i);
  const result = [...floors];
  for (let k = 0; k < deficit; k++) result[order[k]!]!++;
  return result;
}

export function FinancialPage() {
  const navigate = useNavigate();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data: fin, isLoading, isError: finError, refetch: finRefetch } = useQuery({
    queryKey: ["transport", "financial", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportFinancialDto>(
        `/dashboards/transport/financial?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  // Rankings de 30 dias direto da memória de cálculo — MESMA regra do drill (sem duplicar cálculo).
  const { data: due30, isError: due30Error, refetch: due30Refetch } = useQuery({
    queryKey: ["calculation-memory-mini", "recebimentos-previstos-30d"],
    queryFn: () =>
      api.get<CalculationMemoryDto>("/calculation-memory/recebimentos-previstos-30d?limit=8&sortBy=value_desc"),
    staleTime: 60_000,
  });
  const { data: overdueRecent, isError: overdueRecentError, refetch: overdueRecentRefetch } = useQuery({
    queryKey: ["calculation-memory-mini", "inadimplencia-recente-30d"],
    queryFn: () =>
      api.get<CalculationMemoryDto>("/calculation-memory/inadimplencia-recente-30d?limit=8&sortBy=value_desc"),
    staleTime: 60_000,
  });

  // ── Deltas via computeDelta ────────────────────────────────────────────────
  // AR open / AP open: sem data_baixa histórica — delta indisponível por design
  const arOpenDelta  = computeDelta(null, null, { direction: "neutral" });
  const apOpenDelta  = computeDelta(null, null, { direction: "neutral" });

  const receivedDelta = computeDelta(fin?.receivedMtd, fin?.receivedPrevMtd, {
    direction: "higher_is_better",
  });
  const paidDelta = computeDelta(fin?.paidMtd, fin?.paidPrevMtd, {
    direction: "higher_is_worse",
  });
  const arOverdueDelta = computeDelta(fin?.arOverdueTotal, fin?.arOverduePrev1M, {
    direction: "higher_is_worse",
  });
  const apOverdueDelta = computeDelta(fin?.apOverdueTotal, fin?.apOverduePrev1M, {
    direction: "higher_is_worse",
  });

  // Saldo bancário: sem histórico de saldo — delta indisponível por design
  const cashBalDelta = computeDelta(null, null, { direction: "neutral" });

  // Fluxo projetado 30d vs realizado últimos 30d
  const gap30d = fin?.cashGap30d
    ? fin.cashGap30d.receivable - fin.cashGap30d.payable
    : null;
  const gap30dDelta = computeDelta(gap30d, fin?.realizedNet30d, {
    direction: "higher_is_better",
  });

  const aging   = fin?.arAging   ?? [];
  const debtors = fin?.topDebtors ?? [];
  const topDebtor = debtors[0];
  const maxDebtorAmount   = Math.max(...debtors.map((d) => d.overdueAmount), 1);
  const maxSupplierAmount = Math.max(...(fin?.topSuppliers ?? []).map((s) => s.totalPaid), 1);
  const totalSuppliersPaid = (fin?.topSuppliers ?? []).reduce((s, x) => s + x.totalPaid, 0);
  const overdueApCount = (fin?.apSchedule ?? []).find((b) => b.key === "vencidas")?.count ?? 0;
  const monthlyReceived = (fin?.monthlyFlow ?? []).map((m) => m.received);
  const monthlyPaid     = (fin?.monthlyFlow ?? []).map((m) => m.paid);
  const due30List     = useProgressiveList(due30?.breakdown.rows ?? []);
  const overdueList   = useProgressiveList(overdueRecent?.breakdown.rows ?? []);
  const debtorList    = useProgressiveList(debtors);
  const supplierList  = useProgressiveList(fin?.topSuppliers ?? []);
  const monthlyData = (fin?.monthlyFlow && fin.monthlyFlow.length > 0) ? fin.monthlyFlow : [];

  // Pie chart: apply largest-remainder rounding so labels always sum to 100%
  const agingPcts = roundToHundred(aging.map((b) => b.amount));
  const agingWithPct = aging.map((b, i) => ({ ...b, displayPct: agingPcts[i] ?? 0 }));
  const agingPctByBucket = new Map(agingWithPct.map((b) => [b.bucket, b.displayPct]));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Financeiro"
        subtitle="Controle de recebíveis, pagamentos, inadimplência e saldo"
      />

      {(finError || due30Error || overdueRecentError) && <QueryErrorBanner onRetry={() => { finRefetch(); due30Refetch(); overdueRecentRefetch(); }} />}

      {/* KPIs — mesmo padrão visual da Visão Executiva (KpiTile + sparkline/split) */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="A receber em aberto"
          value={fin ? fmtBRL(fin.arOpenTotal) : "—"}
          memory={{ cardKey: "a-receber-em-aberto" }}
          visual={fin && fin.arOpenTotal > 0 ? (
            <SplitMeter parts={[
              { label: "A vencer", value: fin.arOpenTotal - fin.arOverdueTotal, display: fmtCompactBRL(fin.arOpenTotal - fin.arOverdueTotal), tone: "brand" },
              { label: "Vencido",  value: fin.arOverdueTotal,                  display: fmtCompactBRL(fin.arOverdueTotal),                  tone: "danger" },
            ]} />
          ) : null}
          footnote="Posição atual · não segue o período"
          loading={isLoading}
        />
        <KpiTile
          label="A pagar em aberto"
          value={fin ? fmtBRL(fin.apOpenTotal) : "—"}
          memory={{ cardKey: "a-pagar-em-aberto" }}
          visual={fin && fin.apOpenTotal > 0 ? (
            <SplitMeter parts={[
              { label: "A vencer", value: fin.apOpenTotal - fin.apOverdueTotal, display: fmtCompactBRL(fin.apOpenTotal - fin.apOverdueTotal), tone: "muted" },
              { label: "Vencido",  value: fin.apOverdueTotal,                  display: fmtCompactBRL(fin.apOverdueTotal),                  tone: "danger" },
            ]} />
          ) : null}
          footnote="Posição atual · não segue o período"
          loading={isLoading}
        />
        <KpiTile
          label="Recebido no período"
          value={fin ? fmtBRL(fin.receivedMtd) : "—"}
          delta={receivedDelta}
          deltaLabel={fin?.comparisonLabel ?? "vs. período anterior"}
          memory={{ cardKey: "recebido-no-periodo", periodFrom: from, periodTo: to }}
          visual={<SparkLine values={monthlyReceived} label="Recebido nos últimos meses" />}
          footnote="Recebido nos últimos meses. O ponto é o mês atual, ainda em andamento"
          loading={isLoading}
        />
        <KpiTile
          label="Pago no período"
          value={fin ? fmtBRL(fin.paidMtd) : "—"}
          delta={paidDelta}
          deltaLabel={fin?.comparisonLabel ?? "vs. período anterior"}
          memory={{ cardKey: "pago-no-periodo", periodFrom: from, periodTo: to }}
          visual={<SparkLine values={monthlyPaid} label="Pago nos últimos meses" />}
          footnote="Pago nos últimos meses. O ponto é o mês atual, ainda em andamento"
          loading={isLoading}
        />
        <KpiTile
          label="Inadimplência total"
          value={fin ? fmtBRL(fin.arOverdueTotal) : "—"}
          delta={arOverdueDelta}
          deltaLabel="vs. mesmo dia 1 mês atrás"
          status={fin && fin.arOverdueTotal > 0 ? "critical" : null}
          memory={{ cardKey: "inadimplencia-total" }}
          footnote={topDebtor
            ? <>Maior devedor: <span className="font-semibold text-gray-900">{topDebtor.name}</span>, {fmtCompactBRL(topDebtor.overdueAmount)} vencidos há até {topDebtor.oldestDueDays} dias</>
            : "Contas a receber vencidas e não pagas"}
          loading={isLoading}
        />
        <KpiTile
          label="Pagamentos atrasados"
          value={fin ? fmtBRL(fin.apOverdueTotal) : "—"}
          delta={apOverdueDelta}
          deltaLabel="vs. mesmo dia 1 mês atrás"
          status={fin && fin.apOverdueTotal > 0 ? "warning" : null}
          memory={{ cardKey: "pagamentos-atrasados" }}
          footnote={overdueApCount > 0 ? `${overdueApCount} título(s) vencido(s) em aberto` : "Contas a pagar vencidas e não quitadas"}
          loading={isLoading}
        />
        <KpiTile
          label="Saldo bancário"
          value={fin ? fmtBRL(fin.cashBalance) : "—"}
          status={fin && fin.cashBalance < 0 ? "critical" : null}
          memory={{ cardKey: "saldo-bancario" }}
          footnote="Posição atual · não segue o período"
          loading={isLoading}
        />
        <KpiTile
          label="Fluxo projetado 30d"
          value={gap30d != null ? fmtBRL(gap30d) : "—"}
          delta={gap30dDelta}
          deltaLabel="vs. realizado últimos 30 dias"
          status={gap30d != null && gap30d < 0 ? "warning" : null}
          memory={{ cardKey: "fluxo-projetado-30d" }}
          visual={fin?.cashGap30d ? (
            <SplitMeter parts={[
              { label: "A receber 30d", value: Math.max(0, fin.cashGap30d.receivable), display: fmtCompactBRL(fin.cashGap30d.receivable), tone: "brand" },
              { label: "A pagar 30d",   value: Math.max(0, fin.cashGap30d.payable),   display: fmtCompactBRL(fin.cashGap30d.payable),   tone: "muted" },
            ]} />
          ) : null}
          loading={isLoading}
        />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Recebimentos × Pagamentos" subtitle="Últimos 5 meses · barras = fluxo · linha = saldo do mês" height={260}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={monthlyData.map((m) => ({ ...m, net: (m.received ?? 0) - (m.paid ?? 0) }))}
              margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
            >
              <defs>
                <linearGradient id="grad-recebido" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="grad-pago" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#F59E0B" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number, n: string) => [fmtBRL(v), n === "received" ? "Recebido" : n === "paid" ? "Pago" : "Saldo"]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                formatter={(n: string) => (n === "received" ? "Recebido" : n === "paid" ? "Pago" : "Saldo (recebido − pago)")} />
              <Bar dataKey="received" fill="url(#grad-recebido)" radius={[4, 4, 0, 0]} name="received" maxBarSize={36} />
              <Bar dataKey="paid"     fill="url(#grad-pago)"     radius={[4, 4, 0, 0]} name="paid"     maxBarSize={36} />
              <Line type="monotone" dataKey="net" stroke="#0F766E" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="net" />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Inadimplência por Faixa" subtitle="Valores em aberto por aging" height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={agingWithPct}
                dataKey="amount"
                nameKey="bucket"
                cx="50%"
                cy="45%"
                outerRadius={80}
              >
                {agingWithPct.map((_, i) => <Cell key={i} fill={AGING_COLORS[i % AGING_COLORS.length]} />)}
              </Pie>
              <Tooltip
                formatter={(v: number, _: string, entry: { payload?: { bucket?: string; displayPct?: number } }) =>
                  [fmtBRL(v), `${entry.payload?.bucket ?? ""} (${entry.payload?.displayPct ?? 0}%)`]
                }
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                formatter={(value: string) => `${value}: ${agingPctByBucket.get(value) ?? 0}%`}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Contas a pagar: agenda por faixa + compromissos por semana */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Contas a Pagar por Faixa de Vencimento</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Vencimentos até 30 dias · faixas mutuamente exclusivas · clique para ver os títulos
              {fin?.apScheduleSource === "sinersoft"
                ? " · valores do ERP ao vivo (fecham com os cards do topo), e o detalhamento vem do snapshot sincronizado"
                : " · snapshot sincronizado ⚠️ (ERP indisponível, pode divergir dos cards do topo)"}
            </p>
          </div>
          <div className="p-5 space-y-3">
            {isLoading ? (
              <p className="text-sm text-gray-400">Carregando…</p>
            ) : (() => {
              // "Acima de 30 dias" removido do card (não é útil no acompanhamento) — só faixas até 30d.
              const sched = (fin?.apSchedule ?? []).filter((b) => b.key !== "d30_plus");
              if (sched.length === 0) return <p className="text-sm text-gray-400">Sem títulos a pagar nos próximos 30 dias.</p>;
              const maxAmt = Math.max(...sched.map((b) => b.amount), 1);
              const farFuture = (from: string) =>
                new Date(new Date(from).getTime() + 3650 * 86400000).toISOString();
              return sched.map((b) => (
                <div
                  key={b.key}
                  className="cursor-pointer group"
                  title="Ver os títulos desta faixa"
                  onClick={() => {
                    if (b.key === "vencidas") { navigate("/financeiro/memoria/pagamentos-atrasados"); return; }
                    if (!b.from) return;
                    const to = b.to ?? farFuture(b.from);
                    navigate(`/financeiro/memoria/pagamentos-previstos-30d?periodFrom=${encodeURIComponent(b.from)}&periodTo=${encodeURIComponent(to)}`);
                  }}
                >
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 group-hover:text-tm-700">{b.label} <span className="text-gray-400">({b.count})</span></span>
                    <span className="font-semibold text-gray-900">{b.formatted}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${b.key === "vencidas" ? "bg-red-500" : b.key === "hoje" ? "bg-amber-500" : "bg-tm-500"}`}
                      style={{ width: `${Math.max(3, Math.round((b.amount / maxAmt) * 100))}%` }}
                    />
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Compromissos das Próximas Semanas</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Contas a pagar por semana de vencimento (próximos 28 dias) · clique para ver os títulos
              {fin?.apScheduleSource === "sinersoft"
                ? " · valores do ERP ao vivo"
                : " · snapshot sincronizado ⚠️ (ERP indisponível)"}
            </p>
          </div>
          <div className="p-5 space-y-3">
            {isLoading ? (
              <p className="text-sm text-gray-400">Carregando…</p>
            ) : (fin?.apByDueWeek ?? []).length === 0 ? (
              <p className="text-sm text-gray-400">Sem vencimentos nos próximos 28 dias.</p>
            ) : (() => {
              const weeks = fin!.apByDueWeek;
              const maxAmt = Math.max(...weeks.map((w) => w.amount), 1);
              const fmtDay = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;
              return weeks.map((w, i) => {
                const wStart = `${w.week}T00:00:00.000Z`;
                const wEnd = new Date(new Date(wStart).getTime() + 6 * 86400000).toISOString().slice(0, 10);
                const wTo = new Date(new Date(wStart).getTime() + 7 * 86400000).toISOString();
                // "Esta semana" (i=0) começa HOJE — o drill não lista o que já venceu nesta semana.
                const wFrom = i === 0 ? `${new Date().toISOString().slice(0, 10)}T00:00:00.000Z` : wStart;
                return (
                  <div
                    key={w.week}
                    className="cursor-pointer group"
                    title="Ver os títulos a pagar desta semana"
                    onClick={() => navigate(`/financeiro/memoria/pagamentos-previstos-30d?periodFrom=${encodeURIComponent(wFrom)}&periodTo=${encodeURIComponent(wTo)}`)}
                  >
                    <div className="flex justify-between items-baseline text-sm mb-1">
                      <span className="text-gray-600 group-hover:text-tm-700">
                        <span className="font-medium">{i === 0 ? "Esta semana" : `${fmtDay(w.week)}–${fmtDay(wEnd)}`}</span>
                        <span className="text-gray-400"> · {w.count} título(s)</span>
                      </span>
                      <span className="font-semibold text-gray-900">{w.formatted}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${i === 0 ? "bg-amber-500" : "bg-tm-500"}`}
                        style={{ width: `${Math.max(3, Math.round((w.amount / maxAmt) * 100))}%` }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Próximos 30 dias: quem vai pagar × quem deveria ter pago */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Top Clientes a Receber (30 dias)</p>
              <p className="text-xs text-gray-400 mt-0.5">Títulos a vencer de hoje a +30 dias · previsão contratual</p>
            </div>
            <button className="text-xs text-tm-700 hover:underline" onClick={() => navigate("/financeiro/memoria/recebimentos-previstos-30d")}>ver tudo →</button>
          </div>
          <div className="divide-y divide-surface-border">
            {(due30?.breakdown.rows ?? []).length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem títulos a vencer nos próximos 30 dias.</p>
            ) : due30List.visible.map((r, i) => (
              <div key={r.groupKey} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/financeiro/memoria/recebimentos-previstos-30d?group=${encodeURIComponent(r.groupKey)}`)}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{r.groupLabel}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-tm-500 rounded-full transition-all" style={{ width: `${Math.min(Math.max(r.percentageOfTotal, 2), 100)}%` }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-tm-700">{fmtBRL(r.total)}</p>
                  <p className="text-xs text-gray-400">{r.recordCount} título(s) · {r.percentageOfTotal.toFixed(1)}%</p>
                </div>
              </div>
            ))}
            <LoadMoreBar list={due30List} label="clientes" />
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Deveriam Ter Pago (últimos 30 dias)</p>
              <p className="text-xs text-gray-400 mt-0.5">Vencidos nos últimos 30 dias e ainda em aberto · inadimplência recente</p>
            </div>
            <button className="text-xs text-tm-700 hover:underline" onClick={() => navigate("/financeiro/memoria/inadimplencia-recente-30d")}>ver tudo →</button>
          </div>
          <div className="divide-y divide-surface-border">
            {(overdueRecent?.breakdown.rows ?? []).length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Nenhum título venceu sem pagamento nos últimos 30 dias.</p>
            ) : overdueList.visible.map((r, i) => (
              <div key={r.groupKey} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/financeiro/memoria/inadimplencia-recente-30d?group=${encodeURIComponent(r.groupKey)}`)}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{r.groupLabel}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-danger-500 rounded-full transition-all" style={{ width: `${Math.min(Math.max(r.percentageOfTotal, 2), 100)}%` }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-danger-600">{fmtBRL(r.total)}</p>
                  <p className="text-xs text-gray-400">{r.recordCount} título(s) · {r.percentageOfTotal.toFixed(1)}%</p>
                </div>
              </div>
            ))}
            <LoadMoreBar list={overdueList} label="clientes" />
          </div>
        </div>
      </div>

      {/* Top debtors + suppliers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top debtors */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconAlertTriangle className="w-4 h-4 text-danger-600" />
              Top Inadimplentes
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {debtors.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">
                {isLoading ? "Carregando…" : "Nenhum devedor identificado. Sincronização de clientes pendente."}
              </p>
            ) : debtorList.visible.map((d, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                title="Ver composição da inadimplência por cliente"
                onClick={() => navigate("/financeiro/memoria/inadimplencia-total")}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{d.name}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-danger-500 rounded-full transition-all" style={{ width: `${Math.min(Math.max((d.overdueAmount / maxDebtorAmount) * 100, 2), 100)}%` }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-danger-600">{d.formatted}</p>
                  <p className="text-xs text-gray-400">Mais antigo: {d.oldestDueDays} dias</p>
                </div>
              </div>
            ))}
            <LoadMoreBar list={debtorList} label="devedores" />
          </div>
        </div>

        {/* Top suppliers */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconDollar className="w-4 h-4 text-warning-600" />
              Top Fornecedores Pagos
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : (fin?.topSuppliers ?? []).length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados de pagamentos.</p>
            ) : supplierList.visible.map((s, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                title="Ver pagamentos por fornecedor"
                onClick={() => navigate("/financeiro/memoria/pago-no-periodo")}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{s.name}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-warning-500 rounded-full transition-all" style={{ width: `${Math.min(Math.max((s.totalPaid / maxSupplierAmount) * 100, 2), 100)}%` }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-900">{s.formatted}</p>
                  <p className="text-xs text-gray-400">
                    {totalSuppliersPaid > 0 ? `${((s.totalPaid / totalSuppliersPaid) * 100).toFixed(1)}% do total` : "—"}
                  </p>
                </div>
              </div>
            ))}
            <LoadMoreBar list={supplierList} label="fornecedores" />
          </div>
        </div>
      </div>
    </div>
  );
}
